import { Component } from "@angular/core";
import { CanvasService } from "../../services/canvas.service";
import { ElementFactoryService } from "../../services/element-factory.service";
import {
  AnyElement,
  ShapeType,
  Slide,
  TableCellSelection,
  TableCellStyle,
  TableElement,
} from "../../types/canvas.types";
import { CommonService } from "src/app/shared/services/common.service";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "lib-toolbar",
  template: `
    <div class="toolbar">
      <!-- Element Tools -->
      <div class="tool-section">
        <button class="tool-btn" (click)="addTextElement()" title="Add Text">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 4v3h5.5v12h3V7H19V4z" />
          </svg>
        </button>

        <button
          class="tool-btn"
          [class.active]="showShapesMenu"
          (click)="toggleShapesMenu()"
          title="Add Shapes"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <rect x="3" y="6" width="18" height="12" rx="2" />
          </svg>
        </button>

        <div class="inline-shapes-menu" *ngIf="showShapesMenu">
          <button
            class="shape-icon"
            (click)="selectShape('rectangle')"
            title="Rectangle"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <rect x="3" y="6" width="18" height="12" rx="2" />
            </svg>
          </button>

          <button
            class="shape-icon"
            (click)="selectShape('circle')"
            title="Circle"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="9" />
            </svg>
          </button>

          <button
            class="shape-icon"
            (click)="selectShape('triangle')"
            title="Triangle"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l9 20H3z" />
            </svg>
          </button>

          <button
            class="shape-icon"
            (click)="selectShape('diamond')"
            title="Diamond"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l6 10-6 10-6-10z" />
            </svg>
          </button>

          <button class="shape-icon" (click)="selectShape('star')" title="Star">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              />
            </svg>
          </button>

          <button
            class="shape-icon"
            (click)="selectShape('hexagon')"
            title="Hexagon"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.5 3.5L22 12l-4.5 8.5h-11L2 12l4.5-8.5h11z" />
            </svg>
          </button>

          <button
            class="shape-icon"
            (click)="selectShape('ellipse')"
            title="Ellipse"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <ellipse cx="12" cy="12" rx="9" ry="6" />
            </svg>
          </button>

          <button class="shape-icon" (click)="selectShape('line')" title="Line">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <line
                x1="3"
                y1="21"
                x2="21"
                y2="3"
                stroke="currentColor"
                stroke-width="2"
              />
            </svg>
          </button>

          <button
            class="shape-icon"
            (click)="selectShape('arrow')"
            title="Arrow"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <line
                x1="3"
                y1="12"
                x2="21"
                y2="12"
                stroke="currentColor"
                stroke-width="2"
                marker-end="url(#arrowhead)"
              />
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="currentColor" />
                </marker>
              </defs>
            </svg>
          </button>

          <button
            class="shape-icon"
            (click)="selectShape('double-arrow')"
            title="Double Arrow"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <line
                x1="3"
                y1="12"
                x2="21"
                y2="12"
                stroke="currentColor"
                stroke-width="2"
                marker-start="url(#arrowhead-start)"
                marker-end="url(#arrowhead-end)"
              />
              <defs>
                <marker
                  id="arrowhead-start"
                  markerWidth="10"
                  markerHeight="7"
                  refX="1"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="10 0, 0 3.5, 10 7" fill="currentColor" />
                </marker>
                <marker
                  id="arrowhead-end"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="currentColor" />
                </marker>
              </defs>
            </svg>
          </button>
        </div>

        <button
          class="tool-btn"
          [class.active]="showTableMenu"
          (click)="toggleTableMenu()"
          title="Add Table"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M3 4h18a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm1 2v3h16V6H4zm16 5H4v3h16v-3zm0 5H4v3h16v-3z"
            />
          </svg>
        </button>

        <div class="inline-table-menu" *ngIf="showTableMenu">
          <div class="table-menu-field">
            <label>Rows</label>
            <input
              type="number"
              min="1"
              max="20"
              [value]="tableRows"
              (input)="onTableDimInput('rows', $event)"
            />
          </div>
          <div class="table-menu-field">
            <label>Columns</label>
            <input
              type="number"
              min="1"
              max="12"
              [value]="tableColumns"
              (input)="onTableDimInput('columns', $event)"
            />
          </div>
          <button class="apply-btn" (click)="insertTable()">Insert</button>
        </div>
      </div>

      <div class="divider"></div>

      <!-- Zoom Controls -->
      <div class="tool-section zoom-controls">
        <!-- <button
          class="tool-btn"
          (click)="fitCanvas()"
          title="Fit to available space"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M5 3h6v2H7v4H5V3zm14 0v6h-2V5h-4V3h6zM5 21v-6h2v4h4v2H5zm12-6h2v6h-6v-2h4v-4z"
            />
          </svg>
        </button> -->

        <button
          class="tool-btn"
          (click)="zoomOut()"
          [disabled]="!canZoomOut"
          title="Zoom out"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <rect x="5" y="11" width="14" height="2" rx="1" />
          </svg>
        </button>

        <span class="zoom-display" (click)="resetZoom()" title="Reset to 100%">
          {{ zoomDisplay }}
        </span>

        <button
          class="tool-btn"
          (click)="zoomIn()"
          [disabled]="!canZoomIn"
          title="Zoom in"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z" />
          </svg>
        </button>
      </div>

      <div class="divider"></div>

      <!-- Layer Controls -->
      <div class="tool-section" *ngIf="selectedElement">
        <button
          class="tool-btn"
          (click)="bringForward()"
          [disabled]="!canBringForward()"
          title="Bring Forward"
        >
          <svg
            fill="none"
            height="512"
            viewBox="0 0 24 24"
            width="512"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="m11 3c.552 0 1 .448 1 1v2h5c.552 0 1 .448 1 1v5h2c.552 0 1 .448 1 1v7c0 .552-.448 1-1 1h-7c-.552 0-1-.448-1-1v-2h-5c-.552 0-1-.448-1-1v-5h-2c-.552 0-1-.448-1-1v-7c0-.552.448-1 1-1zm5 5h-8v8h8z"
              fill="rgb(0,0,0)"
            />
          </svg>
        </button>

        <button
          class="tool-btn"
          (click)="sendBackward()"
          [disabled]="!canSendBackward()"
          title="Send Backward"
        >
          <svg
            height="100"
            viewBox="0 0 100 100"
            width="100"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g
              id="Full---Rounded---Send-to-Back"
              fill="none"
              fill-rule="evenodd"
            >
              <path
                id="Combined-Shape"
                d="m77.5 50h13c2.4852814 0 4.5 2.0147186 4.5 4.5v36c0 2.4852814-2.0147186 4.5-4.5 4.5h-36c-2.4852814 0-4.5-2.0147186-4.5-4.5v-13h-23c-2.4852814 0-4.5-2.0147186-4.5-4.5v-23h-13c-2.48528137 0-4.5-2.0147186-4.5-4.5v-36c0-2.48528137 2.01471863-4.5 4.5-4.5h36c2.4852814 0 4.5 2.01471863 4.5 4.5v13h23c2.4852814 0 4.5 2.0147186 4.5 4.5zm-36.9264146-9c.1560294-.1743042.2987171-.3609262.4264146-.558298v-26.441702h-27v27zm18.4264146 18v27h27v-27z"
                fill="#000"
              />
            </g>
          </svg>
        </button>

        <button
          class="tool-btn danger"
          (click)="deleteElement()"
          title="Delete Element"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
            />
          </svg>
        </button>
      </div>

      <div class="divider" *ngIf="selectedElement"></div>

      <!-- Text Properties -->
      <div class="tool-section" *ngIf="selectedElement?.type === 'text'">
        <div class="font-size-control">
          <input
            type="range"
            min="8"
            max="72"
            [value]="getTextElement()?.fontSize || 16"
            (input)="updateTextProperty('fontSize', $event)"
            class="range-input"
            title="Adjust Font Size (8px - 72px)"
          />
          <span class="font-size-display"
            >{{ getTextElement()?.fontSize || 16 }}px</span
          >
        </div>

        <select
          [value]="getTextElement()?.fontFamily || 'Inter'"
          (change)="updateTextProperty('fontFamily', $event)"
          class="font-select"
          title="Choose Font Family"
        >
          <optgroup label="Sans Serif">
            <option value="Inter">Inter</option>
            <option value="Roboto">Roboto</option>
            <option value="Open Sans">Open Sans</option>
            <option value="Lato">Lato</option>
            <option value="Montserrat">Montserrat</option>
            <option value="Poppins">Poppins</option>
            <option value="Source Sans Pro">Source Sans Pro</option>
            <option value="Nunito">Nunito</option>
            <option value="Raleway">Raleway</option>
            <option value="Ubuntu">Ubuntu</option>
            <option value="Oswald">Oswald</option>
            <option value="Arial">Arial</option>
          </optgroup>
          <optgroup label="Serif">
            <option value="Merriweather">Merriweather</option>
            <option value="Playfair Display">Playfair Display</option>
            <option value="Crimson Text">Crimson Text</option>
            <option value="Georgia">Georgia</option>
            <option value="Times">Times</option>
          </optgroup>
          <optgroup label="Display & Fun">
            <option value="Dancing Script">Dancing Script</option>
            <option value="Pacifico">Pacifico</option>
            <option value="Lobster">Lobster</option>
            <option value="Righteous">Righteous</option>
            <option value="Bungee">Bungee</option>
            <option value="Fredoka One">Fredoka One</option>
          </optgroup>
        </select>

        <input
          type="color"
          [value]="getTextElement()?.color || '#1f2937'"
          (input)="updateTextProperty('color', $event)"
          class="color-input"
          title="Text Color"
        />

        <button
          class="tool-btn"
          [class.active]="getTextElement()?.bold"
          (click)="toggleTextStyle('bold')"
          title="Bold (Ctrl/Cmd+B)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"
            />
          </svg>
        </button>

        <button
          class="tool-btn"
          [class.active]="getTextElement()?.italic"
          (click)="toggleTextStyle('italic')"
          title="Italic (Ctrl/Cmd+I)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
          </svg>
        </button>

        <button
          class="tool-btn"
          [class.active]="getTextElement()?.underline"
          (click)="toggleTextStyle('underline')"
          title="Underline (Ctrl/Cmd+U)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"
            />
          </svg>
        </button>

        <div class="divider-small"></div>

        <!-- Text Alignment -->
        <button
          class="tool-btn"
          [class.active]="getTextElement()?.textAlign === 'left'"
          (click)="
            updateTextProperty('textAlign', { target: { value: 'left' } })
          "
          title="Align Text Left"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"
            />
          </svg>
        </button>

        <button
          class="tool-btn"
          [class.active]="getTextElement()?.textAlign === 'center'"
          (click)="
            updateTextProperty('textAlign', { target: { value: 'center' } })
          "
          title="Center Align Text"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z"
            />
          </svg>
        </button>

        <button
          class="tool-btn"
          [class.active]="getTextElement()?.textAlign === 'right'"
          (click)="
            updateTextProperty('textAlign', { target: { value: 'right' } })
          "
          title="Align Text Right"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z"
            />
          </svg>
        </button>
      </div>

      <div class="divider" *ngIf="selectedElement?.type === 'text'"></div>

      <div
        class="tool-section"
        *ngIf="selectedTableCell && selectedElement?.type === 'table'"
      >
        <div class="font-size-control">
          <input
            type="range"
            min="8"
            max="72"
            [value]="getSelectedTableCellStyle().fontSize || 14"
            (input)="updateTableCellStyleProperty('fontSize', $event)"
            class="range-input"
            title="Adjust Cell Font Size (8px - 72px)"
          />
          <span class="font-size-display"
            >{{ getSelectedTableCellStyle().fontSize || 14 }}px</span
          >
        </div>

        <select
          [value]="getSelectedTableCellStyle().fontFamily || 'Inter'"
          (change)="updateTableCellStyleProperty('fontFamily', $event)"
          class="font-select"
          title="Choose Cell Font Family"
        >
          <optgroup label="Sans Serif">
            <option value="Inter">Inter</option>
            <option value="Roboto">Roboto</option>
            <option value="Open Sans">Open Sans</option>
            <option value="Lato">Lato</option>
            <option value="Montserrat">Montserrat</option>
            <option value="Poppins">Poppins</option>
            <option value="Source Sans Pro">Source Sans Pro</option>
            <option value="Nunito">Nunito</option>
            <option value="Raleway">Raleway</option>
            <option value="Ubuntu">Ubuntu</option>
            <option value="Oswald">Oswald</option>
            <option value="Arial">Arial</option>
          </optgroup>
          <optgroup label="Serif">
            <option value="Merriweather">Merriweather</option>
            <option value="Playfair Display">Playfair Display</option>
            <option value="Crimson Text">Crimson Text</option>
            <option value="Georgia">Georgia</option>
            <option value="Times">Times</option>
          </optgroup>
          <optgroup label="Display & Fun">
            <option value="Dancing Script">Dancing Script</option>
            <option value="Pacifico">Pacifico</option>
            <option value="Lobster">Lobster</option>
            <option value="Righteous">Righteous</option>
            <option value="Bungee">Bungee</option>
            <option value="Fredoka One">Fredoka One</option>
          </optgroup>
        </select>

        <input
          type="color"
          [value]="getSelectedTableCellStyle().color || '#1f2937'"
          (input)="updateTableCellStyleProperty('color', $event)"
          class="color-input"
          title="Cell Text Color"
        />

        <button
          class="tool-btn"
          [class.active]="getSelectedTableCellStyle().bold"
          (click)="toggleTableCellStyle('bold')"
          title="Bold"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"
            />
          </svg>
        </button>

        <button
          class="tool-btn"
          [class.active]="getSelectedTableCellStyle().italic"
          (click)="toggleTableCellStyle('italic')"
          title="Italic"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
          </svg>
        </button>

        <button
          class="tool-btn"
          [class.active]="getSelectedTableCellStyle().underline"
          (click)="toggleTableCellStyle('underline')"
          title="Underline"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"
            />
          </svg>
        </button>

        <div class="divider-small"></div>

        <button
          class="tool-btn"
          [class.active]="getSelectedTableCellStyle().textAlign === 'left'"
          (click)="setTableCellAlignment('left')"
          title="Align Cell Left"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"
            />
          </svg>
        </button>

        <button
          class="tool-btn"
          [class.active]="getSelectedTableCellStyle().textAlign === 'center'"
          (click)="setTableCellAlignment('center')"
          title="Align Cell Center"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z"
            />
          </svg>
        </button>

        <button
          class="tool-btn"
          [class.active]="getSelectedTableCellStyle().textAlign === 'right'"
          (click)="setTableCellAlignment('right')"
          title="Align Cell Right"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z"
            />
          </svg>
        </button>
      </div>

      <div
        class="divider"
        *ngIf="selectedTableCell && selectedElement?.type === 'table'"
      ></div>

      <!-- Shape Properties -->
      <div class="tool-section" *ngIf="selectedElement?.type === 'shape'">
        <button
          class="tool-btn transparent-btn"
          [class.active]="getShapeElement()?.fill === 'transparent'"
          (click)="toggleTransparentFill()"
          title="Toggle Shape Fill (Transparent/Solid)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            id="fill-colour"
          >
            <g id="fill_colour">
              <polyline
                points="144 144 144 16 496 16 496 368 368 368"
                style="fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;stroke-width:32px"
              ></polyline>
              <rect
                width="352"
                height="352"
                x="16"
                y="144"
                style="fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;stroke-width:32px"
              ></rect>
              <polyline
                points="208 144 208 80 432 80 432 304 368 304"
                style="fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;stroke-width:32px"
              ></polyline>
            </g>
          </svg>
        </button>

        <div class="color-button-wrapper">
          <button
            class="tool-btn color-btn fill-btn"
            (click)="openFillColorPicker()"
            [disabled]="getShapeElement()?.fill === 'transparent'"
            title="Shape Fill Color"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              id="border-color"
            >
              <path fill="none" d="M0 0h24v24H0V0z"></path>
              <path
                d="M17.75 7 14 3.25 4.15 13.1c-.1.1-.15.22-.15.36v3.04c0 .28.22.5.5.5h3.04c.13 0 .26-.05.35-.15L17.75 7zm2.96-2.96a.996.996 0 0 0 0-1.41L18.37.29a.996.996 0 0 0-1.41 0L15 2.25 18.75 6l1.96-1.96z"
              ></path>
              <path
                fill-opacity=".36"
                d="M2 20h20c1.1 0 2 .9 2 2s-.9 2-2 2H2c-1.1 0-2-.9-2-2s.9-2 2-2z"
              ></path>
            </svg>
            <div
              class="color-indicator fill-indicator"
              [style.background-color]="getShapeElement()?.fill || '#2563eb'"
              [style.opacity]="
                getShapeElement()?.fill === 'transparent' ? '0.3' : '1'
              "
            ></div>
          </button>
          <input
            type="color"
            [value]="getShapeElement()?.fill || '#2563eb'"
            (input)="updateShapeProperty('fill', $event)"
            [disabled]="getShapeElement()?.fill === 'transparent'"
            class="hidden-color-input"
            #fillColorInput
            title="Shape Fill Color"
          />
        </div>

        <div class="color-button-wrapper">
          <button
            class="tool-btn color-btn border-btn"
            (click)="openBorderColorPicker()"
            title="Shape Border Color"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              id="border-color"
            >
              <path fill="none" d="M0 0h24v24H0V0z"></path>
              <path
                d="M17.75 7 14 3.25 4.15 13.1c-.1.1-.15.22-.15.36v3.04c0 .28.22.5.5.5h3.04c.13 0 .26-.05.35-.15L17.75 7zm2.96-2.96a.996.996 0 0 0 0-1.41L18.37.29a.996.996 0 0 0-1.41 0L15 2.25 18.75 6l1.96-1.96z"
              ></path>
              <path
                fill-opacity=".36"
                d="M2 20h20c1.1 0 2 .9 2 2s-.9 2-2 2H2c-1.1 0-2-.9-2-2s.9-2 2-2z"
              ></path>
            </svg>
            <div
              class="color-indicator border-indicator"
              [style.background-color]="getShapeElement()?.stroke || '#1d4ed8'"
            ></div>
          </button>
          <input
            type="color"
            [value]="getShapeElement()?.stroke || '#1d4ed8'"
            (input)="updateShapeProperty('stroke', $event)"
            class="hidden-color-input"
            #borderColorInput
            title="Shape Border Color"
          />
        </div>

        <input
          type="range"
          min="0"
          max="10"
          [value]="getShapeElement()?.strokeWidth || 2"
          (input)="updateShapeProperty('strokeWidth', $event)"
          class="range-input"
          title="Border Width (0px - 10px)"
        />
      </div>

      <div class="spacer"></div>

      <!-- Save/Load - Hidden -->
      <div class="tool-section" style="display: none;">
        <button
          class="tool-btn primary"
          (click)="saveProject()"
          title="Save {{ displayLabels?.project | uppercase }}"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"
            />
          </svg>
        </button>

        <input
          type="file"
          accept=".json"
          (change)="loadProject($event)"
          #fileInput
          style="display: none;"
        />

        <button
          class="tool-btn"
          (click)="fileInput.click()"
          title="Load {{ displayLabels?.project | uppercase }}"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"
            />
          </svg>
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        /* Import CSS variables from the main app */
        --canvas-primary: #232459;
        --canvas-primary-light: #3a3b80;
        --canvas-primary-dark: #14155e;
        --canvas-accent: #706fd3;
        --canvas-accent-light: #8585da;
        --canvas-accent-dark: #5d5cc8;
        --canvas-success: #10b981;
        --canvas-success-dark: #059669;
        --canvas-error: #ef4444;
        --canvas-error-dark: #dc2626;
        --canvas-gray-100: #f3f4f6;
        --canvas-gray-300: #d1d5db;
        --canvas-gray-500: #6b7280;
        --canvas-space-1: 0.25rem;
        --canvas-space-2: 0.5rem;
        --canvas-space-3: 0.75rem;
        --canvas-space-4: 1rem;
        --canvas-text-xs: 0.75rem;
        --canvas-text-sm: 0.875rem;
        --canvas-font-medium: 500;
        --canvas-font-semibold: 600;
        --canvas-radius: 0.375rem;
        --canvas-radius-sm: 0.25rem;
        --canvas-radius-lg: 0.75rem;
        --canvas-transition: 200ms ease;
        --canvas-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        --canvas-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        --canvas-z-dropdown: 1000;
        display: block;
        width: 100%;
      }

      .toolbar {
        height: 64px;
        background: #f1f5f9;
        color: #1f2937;
        padding: 0 1rem;
        display: flex;
        align-items: center;
        gap: 1rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        border-bottom: 1px solid #e2e8f0;
        overflow-x: auto;
        overflow-y: visible;
        position: relative;
        z-index: 1000;
        width: 100%;
      }

      .tool-section {
        display: flex;
        align-items: center;
        gap: var(--canvas-space-2);
        flex-shrink: 0;
      }

      .zoom-controls {
        gap: var(--canvas-space-2);
      }

      .zoom-controls .tool-btn {
        min-width: 36px;
        height: 36px;
      }

      .zoom-display {
        padding: 0 0.75rem;
        height: 36px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--canvas-radius-sm);
        background: #ffffff;
        border: 1px solid #d1d5db;
        color: #374151;
        font-weight: var(--canvas-font-semibold);
        letter-spacing: 0.02em;
        cursor: pointer;
        user-select: none;
        transition:
          background var(--canvas-transition),
          color var(--canvas-transition),
          border-color var(--canvas-transition);
        min-width: 72px;
        text-align: center;
      }

      .zoom-display:hover {
        background: #e5e7eb;
        border-color: #9ca3af;
        color: #111827;
      }

      .divider {
        width: 1px;
        height: 32px;
        background: #d1d5db;
        flex-shrink: 0;
      }

      .divider-small {
        width: 1px;
        height: 20px;
        background: #d1d5db;
        margin: 0 var(--canvas-space-1);
        flex-shrink: 0;
      }

      .spacer {
        flex: 1;
        min-width: var(--canvas-space-4);
      }

      .tool-btn {
        width: 40px;
        height: 40px;
        background: #ffffff;
        color: #374151;
        border: 1px solid #d1d5db;
        border-radius: var(--canvas-radius);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all var(--canvas-transition);
        position: relative;
        flex-shrink: 0;
      }

      .tool-btn:hover {
        background: #e5e7eb;
        border-color: #9ca3af;
        color: #111827;
        transform: translateY(-1px);
        box-shadow: var(--canvas-shadow);
      }

      .tool-btn.active {
        background: var(--canvas-accent);
        color: white;
        border-color: var(--canvas-accent);
        box-shadow: 0 0 0 2px var(--canvas-accent-light);
        transform: translateY(-1px);
      }

      .tool-btn.active:hover {
        background: var(--canvas-accent-dark);
        box-shadow: 0 0 0 2px var(--canvas-accent);
      }

      .tool-btn.primary {
        background: var(--canvas-success);
        color: white;
      }

      .tool-btn.primary:hover {
        background: var(--canvas-success-dark);
      }

      .tool-btn.danger {
        background: var(--canvas-error);
        color: white;
      }

      .tool-btn.danger:hover {
        background: var(--canvas-error-dark);
      }

      .tool-btn:disabled {
        background: #f9fafb;
        color: #9ca3af;
        border-color: #e5e7eb;
        cursor: not-allowed;
        transform: none;
        opacity: 0.6;
      }

      .tool-btn:disabled:hover {
        background: #f9fafb;
        border-color: #e5e7eb;
        color: #9ca3af;
        transform: none;
        box-shadow: none;
      }

      .tool-btn:focus-visible {
        outline: none;
        box-shadow:
          0 0 0 2px white,
          0 0 0 4px var(--canvas-accent);
      }

      .font-size-control {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--canvas-space-1);
        width: 80px;
        flex-shrink: 0;
      }

      .font-size-display {
        font-size: var(--canvas-text-xs);
        color: #6b7280;
        font-weight: var(--canvas-font-medium);
        min-width: 30px;
        text-align: center;
      }

      .range-input {
        width: 100%;
        accent-color: var(--canvas-accent);
        height: 4px;
        background: #e5e7eb;
        border-radius: var(--canvas-radius-sm);
        outline: none;
        cursor: pointer;
      }

      .range-input::-webkit-slider-thumb {
        appearance: none;
        width: 16px;
        height: 16px;
        background: var(--canvas-accent);
        border-radius: 50%;
        cursor: pointer;
        box-shadow: var(--canvas-shadow-sm);
      }

      .range-input::-moz-range-thumb {
        width: 16px;
        height: 16px;
        background: var(--canvas-accent);
        border-radius: 50%;
        cursor: pointer;
        border: none;
        box-shadow: var(--canvas-shadow-sm);
      }

      .font-select {
        padding: var(--canvas-space-2) var(--canvas-space-3);
        background: #ffffff;
        color: #374151;
        border: 1px solid #d1d5db;
        border-radius: var(--canvas-radius);
        font-size: var(--canvas-text-sm);
        min-width: 160px;
        max-width: 200px;
        cursor: pointer;
        flex-shrink: 0;
      }

      .font-select:focus {
        outline: none;
        border-color: var(--canvas-accent);
        box-shadow: 0 0 0 2px rgba(112, 111, 211, 0.1);
      }

      .font-select optgroup {
        background: #f9fafb;
        color: #374151;
        font-weight: var(--canvas-font-semibold);
        font-size: var(--canvas-text-xs);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .font-select option {
        background: #ffffff;
        color: #374151;
        padding: var(--canvas-space-1) var(--canvas-space-2);
      }

      .font-select option:hover {
        background: #f3f4f6;
      }

      .color-input {
        width: 40px;
        height: 40px;
        border: none;
        border-radius: var(--canvas-radius);
        cursor: pointer;
        background: none;
        padding: var(--canvas-space-1);
        flex-shrink: 0;
      }

      .color-input::-webkit-color-swatch {
        border: none;
        border-radius: var(--canvas-radius-sm);
      }

      .color-input::-webkit-color-swatch-wrapper {
        padding: 0;
        border: 2px solid #d1d5db;
        border-radius: var(--canvas-radius-sm);
      }

      .color-input:focus {
        outline: none;
        box-shadow:
          0 0 0 2px white,
          0 0 0 4px var(--canvas-accent);
      }

      .transparent-btn {
        position: relative;
        overflow: visible;
      }

      .transparent-btn::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: 2px;
        height: 20px;
        background: var(--canvas-error);
        transform: translate(-50%, -50%) rotate(45deg);
        opacity: 0;
        transition: opacity var(--canvas-transition);
        z-index: 2;
      }

      .transparent-btn.active::before {
        opacity: 1;
      }

      .color-button-wrapper {
        position: relative;
        display: inline-block;
      }

      .color-btn {
        position: relative;
        padding-bottom: 8px; /* Make room for color indicator */
      }

      .color-indicator {
        position: absolute;
        bottom: 2px;
        left: 50%;
        transform: translateX(-50%);
        width: 20px;
        height: 3px;
        border-radius: 1px;
        border: 1px solid rgba(0, 0, 0, 0.1);
      }

      .fill-indicator {
        background: var(--canvas-accent);
      }

      .border-indicator {
        background: var(--canvas-primary);
      }

      .hidden-color-input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
        width: 1px;
        height: 1px;
      }

      .fill-btn:disabled {
        opacity: 0.5;
      }

      .fill-btn:disabled .color-indicator {
        opacity: 0.3;
      }

      .color-input:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .inline-shapes-menu {
        display: flex;
        align-items: center;
        gap: var(--canvas-space-1);
        margin-left: var(--canvas-space-2);
        padding-left: var(--canvas-space-2);
        border-left: 1px solid #d1d5db;
        animation: slideIn 200ms ease;
      }

      .inline-table-menu {
        display: flex;
        align-items: center;
        gap: var(--canvas-space-2);
        margin-left: var(--canvas-space-2);
        padding: var(--canvas-space-2) var(--canvas-space-3);
        border-left: 1px solid #d1d5db;
        background: #ffffff;
        border: 1px solid #d1d5db;
        border-radius: var(--canvas-radius);
        box-shadow: var(--canvas-shadow);
        animation: slideIn 200ms ease;
      }

      .table-menu-field {
        display: flex;
        flex-direction: column;
        gap: var(--canvas-space-1);
        color: #374151;
        font-size: var(--canvas-text-xs);
      }

      .table-menu-field input {
        width: 64px;
        padding: var(--canvas-space-1) var(--canvas-space-2);
        border-radius: var(--canvas-radius-sm);
        border: 1px solid #d1d5db;
        background: #ffffff;
        color: #374151;
        text-align: center;
        font-weight: var(--canvas-font-semibold);
      }

      .table-menu-field input:focus {
        outline: none;
        border-color: var(--canvas-accent);
        box-shadow: 0 0 0 2px rgba(112, 111, 211, 0.1);
      }

      .apply-btn {
        padding: var(--canvas-space-2) var(--canvas-space-3);
        background: var(--canvas-accent);
        color: white;
        border: none;
        border-radius: var(--canvas-radius-sm);
        cursor: pointer;
        font-size: var(--canvas-text-sm);
        font-weight: var(--canvas-font-semibold);
        transition: all var(--canvas-transition);
      }

      .apply-btn:hover {
        background: var(--canvas-accent-dark);
        transform: translateY(-1px);
        box-shadow: var(--canvas-shadow);
      }

      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateX(-10px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      .shape-icon {
        width: 36px;
        height: 36px;
        background: none;
        color: #6b7280;
        border: none;
        border-radius: var(--canvas-radius);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all var(--canvas-transition);
        flex-shrink: 0;
      }

      .shape-icon:hover {
        background: #ffffff;
        color: #374151;
        border: 1px solid #d1d5db;
        transform: scale(1.05);
      }

      .shape-icon svg {
        color: currentColor;
      }

      /* Mobile responsive */
      @media (max-width: 640px) {
        .toolbar {
          height: 56px;
          padding: 0 var(--canvas-space-2);
          gap: var(--canvas-space-2);
        }

        .tool-btn {
          width: 36px;
          height: 36px;
        }

        .tool-section {
          gap: var(--canvas-space-1);
        }

        .font-size-control {
          width: 60px;
        }

        .font-select {
          min-width: 120px;
          font-size: var(--canvas-text-xs);
        }

        .spacer {
          min-width: var(--canvas-space-2);
        }
      }

      /* Tablet responsive */
      @media (min-width: 641px) and (max-width: 768px) {
        .toolbar {
          padding: 0 var(--canvas-space-3);
          gap: var(--canvas-space-3);
        }
      }

      /* Hide some tools on very small screens */
      @media (max-width: 480px) {
        .tool-section:nth-child(n + 4) {
          display: none;
        }

        .font-select {
          display: none;
        }
      }

      /* Ensure proper font loading for font previews */
      .font-select option[value="Inter"] {
        font-family: "Inter", sans-serif;
      }
      .font-select option[value="Roboto"] {
        font-family: "Roboto", sans-serif;
      }
      .font-select option[value="Open Sans"] {
        font-family: "Open Sans", sans-serif;
      }
      .font-select option[value="Lato"] {
        font-family: "Lato", sans-serif;
      }
      .font-select option[value="Montserrat"] {
        font-family: "Montserrat", sans-serif;
      }
      .font-select option[value="Poppins"] {
        font-family: "Poppins", sans-serif;
      }
      .font-select option[value="Source Sans Pro"] {
        font-family: "Source Sans Pro", sans-serif;
      }
      .font-select option[value="Nunito"] {
        font-family: "Nunito", sans-serif;
      }
      .font-select option[value="Raleway"] {
        font-family: "Raleway", sans-serif;
      }
      .font-select option[value="Ubuntu"] {
        font-family: "Ubuntu", sans-serif;
      }
      .font-select option[value="Oswald"] {
        font-family: "Oswald", sans-serif;
      }
      .font-select option[value="Merriweather"] {
        font-family: "Merriweather", serif;
      }
      .font-select option[value="Playfair Display"] {
        font-family: "Playfair Display", serif;
      }
      .font-select option[value="Crimson Text"] {
        font-family: "Crimson Text", serif;
      }
      .font-select option[value="Dancing Script"] {
        font-family: "Dancing Script", cursive;
      }
      .font-select option[value="Pacifico"] {
        font-family: "Pacifico", cursive;
      }
      .font-select option[value="Lobster"] {
        font-family: "Lobster", cursive;
      }
      .font-select option[value="Righteous"] {
        font-family: "Righteous", cursive;
      }
      .font-select option[value="Bungee"] {
        font-family: "Bungee", cursive;
      }
      .font-select option[value="Fredoka One"] {
        font-family: "Fredoka One", cursive;
      }
      .font-select option[value="Arial"] {
        font-family: "Arial", sans-serif;
      }
      .font-select option[value="Georgia"] {
        font-family: "Georgia", serif;
      }
      .font-select option[value="Times"] {
        font-family: "Times", serif;
      }
    `,
  ],
})
export class ToolbarComponent {
  selectedElement: AnyElement | null = null;
  currentSlide: Slide | null = null;
  showShapesMenu = false;
  showTableMenu = false;
  tableRows = 3;
  tableColumns = 3;
  selectedTableCell: TableCellSelection | null = null;
  zoomLevel = 1;
  zoomStep = 0.1;
  minZoom = 0.25;
  maxZoom = 3;
  private readonly defaultTableCellStyle: TableCellStyle = {
    fontSize: 14,
    fontFamily: "Inter",
    color: "#1f2937",
    textAlign: "left",
    bold: false,
    italic: false,
    underline: false,
  };
  displayLabels: any;

  constructor(
    private canvasService: CanvasService,
    private elementFactory: ElementFactoryService,
    private readonly commonService: CommonService,
    public readonly toastrService: ToastrService
  ) {
    this.minZoom = this.canvasService.minZoom;
    this.maxZoom = this.canvasService.maxZoom;

    this.canvasService.selectedElement$.subscribe((element) => {
      this.selectedElement = element;
    });

    this.canvasService.currentSlide$.subscribe((slide) => {
      this.currentSlide = slide;
    });

    this.canvasService.selectedTableCell$.subscribe((selection) => {
      this.selectedTableCell = selection;
    });

    this.canvasService.zoom$.subscribe((level) => {
      this.zoomLevel = level;
    });

    // Close shapes menu when clicking outside
    document.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".inline-shapes-menu") &&
        !target.closest("button[title='Add Shapes']")
      ) {
        this.showShapesMenu = false;
      }

      if (
        !target.closest(".inline-table-menu") &&
        !target.closest("button[title='Add Table']")
      ) {
        this.showTableMenu = false;
      }
    });
  }

  get zoomDisplay(): string {
    return `${Math.round(this.zoomLevel * 100)}%`;
  }

  get canZoomOut(): boolean {
    return this.zoomLevel > this.minZoom + 0.001;
  }

  get canZoomIn(): boolean {
    return this.zoomLevel < this.maxZoom - 0.001;
  }

  zoomOut(): void {
    this.canvasService.zoomOut(this.zoomStep);
  }

  zoomIn(): void {
    this.canvasService.zoomIn(this.zoomStep);
  }

  resetZoom(): void {
    this.canvasService.resetZoom();
  }

  fitCanvas(): void {
    this.canvasService.requestFitToViewport();
  }

  addTextElement(): void {
    const element = this.elementFactory.createTextElement({ x: 100, y: 100 });
    this.canvasService.addElement(element);
  }

  addShape(shapeType: ShapeType): void {
    const element = this.elementFactory.createShapeElement(
      { x: 150, y: 150 },
      shapeType
    );
    this.canvasService.addElement(element);
  }

  bringForward(): void {
    if (this.selectedElement) {
      this.canvasService.bringForward(this.selectedElement.id);
    }
  }

  sendBackward(): void {
    if (this.selectedElement) {
      this.canvasService.sendBackward(this.selectedElement.id);
    }
  }

  deleteElement(): void {
    if (this.selectedElement) {
      this.canvasService.deleteElement(this.selectedElement.id);
    }
  }

  getTextElement(): any {
    return this.selectedElement?.type === "text" ? this.selectedElement : null;
  }

  getShapeElement(): any {
    return this.selectedElement?.type === "shape" ? this.selectedElement : null;
  }

  getTableElement(): TableElement | null {
    return this.selectedElement?.type === "table"
      ? (this.selectedElement as TableElement)
      : null;
  }

  getSelectedTableCellStyle(): TableCellStyle {
    if (!this.selectedTableCell) {
      return this.defaultTableCellStyle;
    }

    const tableElement = this.getTableElement();
    if (!tableElement || tableElement.id !== this.selectedTableCell.elementId) {
      return this.defaultTableCellStyle;
    }

    const { row, col } = this.selectedTableCell;
    return tableElement.cellStyles?.[row]?.[col] || this.defaultTableCellStyle;
  }

  updateTextProperty(property: string, event: any): void {
    if (!this.selectedElement || this.selectedElement.type !== "text") return;

    let value = event.target ? event.target.value : event;

    // Handle numeric properties
    if (property === "fontSize") {
      value = parseInt(value, 10);
      if (isNaN(value) || value < 8) value = 8;
      if (value > 72) value = 72;
    }

    this.canvasService.updateElement(this.selectedElement.id, {
      [property]: value,
    });
  }

  updateShapeProperty(property: string, event: any): void {
    if (!this.selectedElement || this.selectedElement.type !== "shape") return;

    let value = event.target ? event.target.value : event;

    // Handle numeric properties
    if (property === "strokeWidth") {
      value = parseInt(value, 10);
      if (isNaN(value) || value < 0) value = 0;
      if (value > 10) value = 10;
    }

    this.canvasService.updateElement(this.selectedElement.id, {
      [property]: value,
    });
  }

  updateTableCellStyleProperty(
    property: keyof TableCellStyle,
    eventOrValue: any
  ): void {
    if (!this.selectedTableCell) return;

    const tableElement = this.getTableElement();
    if (!tableElement || tableElement.id !== this.selectedTableCell.elementId) {
      return;
    }

    let value: any;
    if (
      eventOrValue &&
      typeof eventOrValue === "object" &&
      "target" in eventOrValue
    ) {
      value = (eventOrValue.target as HTMLInputElement).value;
    } else {
      value = eventOrValue;
    }

    if (property === "fontSize") {
      value = parseInt(value, 10);
      if (isNaN(value)) {
        return;
      }
      value = Math.max(8, Math.min(value, 72));
    }

    const updates: Partial<TableCellStyle> = {
      [property]: value,
    } as Partial<TableCellStyle>;

    this.canvasService.updateTableCellStyle(
      this.selectedTableCell.elementId,
      this.selectedTableCell.row,
      this.selectedTableCell.col,
      updates
    );
  }

  toggleTableCellStyle(style: "bold" | "italic" | "underline"): void {
    const current = this.getSelectedTableCellStyle();
    this.updateTableCellStyleProperty(style, !current[style]);
  }

  setTableCellAlignment(alignment: "left" | "center" | "right"): void {
    this.updateTableCellStyleProperty("textAlign", alignment);
  }

  toggleTextStyle(style: "bold" | "italic" | "underline"): void {
    const textElement = this.getTextElement();
    if (!textElement) return;

    const currentValue = textElement[style] || false;

    // Update the specific style property
    const updates: any = {
      [style]: !currentValue,
    };

    // If toggling bold, also update fontWeight for consistency
    if (style === "bold") {
      updates.fontWeight = !currentValue ? "700" : "400";
    }

    // Update the element - the canvas service will handle updating the selected element
    this.canvasService.updateElement(textElement.id, updates);
  }

  saveProject(): void {
    const json = this.canvasService.exportToJSON();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "canvas-project.json";
    link.click();

    URL.revokeObjectURL(url);
  }

  loadProject(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.commonService.validateFileSize(file).subscribe((isValid) => {
        if (!isValid) {
          this.toastrService.error("Please upload less than 50MB");
          return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          const success = this.canvasService.loadFromJSON(content);

          if (!success) {
            this.displayLabels = this.commonService.getLabels();
            alert(`failed to load ${this.displayLabels?.project}. Please check the file format.`);
          }
        };
        reader.readAsText(file);
      });
    }
  }

  public canBringForward(): boolean {
    if (!this.selectedElement || !this.currentSlide) return false;

    const maxZIndex = Math.max(
      ...this.currentSlide.elements.map((el: any) => el.zIndex)
    );
    return this.selectedElement.zIndex < maxZIndex;
  }

  public canSendBackward(): boolean {
    if (!this.selectedElement || !this.currentSlide) return false;

    const minZIndex = Math.min(
      ...this.currentSlide.elements.map((el: any) => el.zIndex)
    );
    return this.selectedElement.zIndex > minZIndex;
  }

  public toggleTransparentFill(): void {
    const shapeElement = this.getShapeElement();
    if (!shapeElement) return;

    const newFill =
      shapeElement.fill === "transparent" ? "#2563eb" : "transparent";
    this.canvasService.updateElement(shapeElement.id, { fill: newFill });
  }

  public openFillColorPicker(): void {
    // Programmatically click the hidden color input
    const fillColorInput = document.querySelector(
      '.hidden-color-input[title="Shape Fill Color"]'
    ) as HTMLInputElement;
    if (fillColorInput) {
      fillColorInput.click();
    }
  }

  public openBorderColorPicker(): void {
    // Programmatically click the hidden color input
    const borderColorInput = document.querySelector(
      '.hidden-color-input[title="Shape Border Color"]'
    ) as HTMLInputElement;
    if (borderColorInput) {
      borderColorInput.click();
    }
  }

  toggleShapesMenu(): void {
    this.showShapesMenu = !this.showShapesMenu;
  }

  selectShape(shapeType: ShapeType): void {
    this.addShape(shapeType);
    this.showShapesMenu = false;
  }

  toggleTableMenu(): void {
    this.showTableMenu = !this.showTableMenu;
    if (this.showTableMenu) {
      this.showShapesMenu = false;
    }
  }

  onTableDimInput(dimension: "rows" | "columns", event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = parseInt(input.value, 10);
    if (Number.isNaN(value)) {
      return;
    }

    if (dimension === "rows") {
      this.tableRows = Math.max(1, Math.min(value, 20));
      input.value = String(this.tableRows);
    } else {
      this.tableColumns = Math.max(1, Math.min(value, 12));
      input.value = String(this.tableColumns);
    }
  }

  insertTable(): void {
    const element = this.elementFactory.createTableElement(
      { x: 180, y: 160 },
      this.tableRows,
      this.tableColumns
    );
    this.canvasService.addElement(element);
    this.showTableMenu = false;
  }
}
